import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** The part of an RTK Query result the picker reads. */
export interface AsyncSearchResult<T> {
  data?: T[];
  isLoading?: boolean;
  isFetching?: boolean;
}

export interface AsyncMultiPickerProps<T> {
  label: string;
  /** Server search; called on every render with the debounced term. */
  useSearch: (term: string) => AsyncSearchResult<T>;
  getOptionLabel: (option: T) => string;
  getOptionKey: (option: T) => string;
  value: T[];
  onChange: (value: T[]) => void;
  /** Secondary content for an option (school, period, role). */
  renderOptionExtra?: (option: T) => ReactNode;
  placeholder?: string;
  disabled?: boolean;
  helperText?: ReactNode;
  /** Debounce in ms before the term reaches `useSearch`. Defaults to 300. */
  delay?: number;
}

const withoutKey = <P extends { key?: unknown }>({
  key: _key,
  ...rest
}: P): Omit<P, "key"> => rest;

const useDebouncedValue = <V,>(value: V, delay: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

/**
 * Multi-select over a server search: picked options show as chips, the list shows loading and
 * "No matches" states, and arrow keys, Enter and Backspace work as in any combobox.
 */
export const AsyncMultiPicker = <T,>({
  label,
  useSearch,
  getOptionLabel,
  getOptionKey,
  value,
  onChange,
  renderOptionExtra,
  placeholder,
  disabled,
  helperText,
  delay = 300,
}: AsyncMultiPickerProps<T>) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const term = useDebouncedValue(inputValue.trim(), delay);
  const { data, isLoading, isFetching } = useSearch(term);
  const searching =
    Boolean(isLoading || isFetching) || term !== inputValue.trim();

  return (
    <Autocomplete<T, true, false, false>
      multiple
      disabled={disabled}
      options={data ?? []}
      value={value}
      onChange={(_event, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      // The server already filtered by the term.
      filterOptions={(options) => options}
      filterSelectedOptions
      disableCloseOnSelect
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, selected) =>
        getOptionKey(option) === getOptionKey(selected)
      }
      loading={searching}
      loadingText={t("common.loading")}
      noOptionsText={t("picker.noMatches")}
      renderOption={(props, option) => {
        // MUI passes `key` inside the props; React wants it set directly.
        const optionProps = withoutKey(props);
        return (
          <Box component="li" {...optionProps} key={getOptionKey(option)}>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <span>{getOptionLabel(option)}</span>
              {renderOptionExtra ? (
                <Box
                  component="span"
                  sx={{ fontSize: 13, color: "text.secondary" }}
                >
                  {renderOptionExtra(option)}
                </Box>
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => {
          const tagProps = withoutKey(getTagProps({ index }));
          return (
            <Chip
              {...tagProps}
              key={getOptionKey(option)}
              size="small"
              label={getOptionLabel(option)}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : undefined}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {searching ? (
                  <CircularProgress
                    color="inherit"
                    size={18}
                    aria-hidden="true"
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AsyncMultiPicker;
